import type { MeDto } from '@af/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/dropdown';
import { LANGUAGES, setLanguage } from '@/i18n';
import { activeOrg } from '@/lib/active-org';
import { initials } from '@/lib/agent-utils';
import { auth } from '@/lib/auth';
import { useCompanies } from '@/lib/queries';
import { THEME_PREFS, type ThemePref, useTheme } from '@/lib/theme';

/**
 * Switch this tab's company (resets all cached queries). The tab is pinned via `x-af-org`, so other
 * open tabs stay in their own company; the session default only decides where new tabs open.
 */
export function useSwitchOrg(currentId: string | undefined) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (id: string, to: '/' | '/onboarding' = '/') => {
    if (id === currentId) return;
    try {
      await auth.setActiveOrganization(id);
    } catch (e) {
      toast.error((e as Error).message);
      return;
    }
    activeOrg.set(id);
    await qc.resetQueries();
    void navigate({ to });
  };
}

/** Avatar with a coloured ring (top bar). */
export function RingAvatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span className="avatar-ring inline-flex shrink-0">
      <span
        style={{ width: size, height: size }}
        className="flex items-center justify-center rounded-full bg-accent text-2xs font-semibold text-accent-fg ring-2 ring-bg"
      >
        {initials(name)}
      </span>
    </span>
  );
}

export function UserMenu({ me }: { me: MeDto }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const orgs = useCompanies();
  const switchOrg = useSwitchOrg(me.organization?.id);
  const theme = useTheme();

  const signOut = async () => {
    await auth.signOut().catch(() => {});
    qc.clear();
    void navigate({ to: '/login', search: {} });
  };

  return (
    <Menu>
      <MenuTrigger aria-label={t('shell.userMenu')} className="ml-0.5 inline-flex rounded-full">
        <RingAvatar name={me.user.name} />
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>
          {me.user.email} · {me.role}
        </MenuLabel>
        <MenuSeparator />
        <MenuLabel>{t('shell.switchOrg')}</MenuLabel>
        <MenuRadioGroup value={me.organization?.id ?? ''} onValueChange={(v) => void switchOrg(v)}>
          {(orgs.data ?? (me.organization ? [me.organization] : [])).map((o) => (
            <MenuRadioItem key={o.id} value={o.id}>
              {o.name}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        <MenuItem onSelect={() => void navigate({ to: '/companies' })}>{t('companies.all')}</MenuItem>
        <MenuSeparator />
        <MenuLabel>{t('theme.label')}</MenuLabel>
        <MenuRadioGroup value={theme.pref} onValueChange={(v) => theme.setPref(v as ThemePref)}>
          {THEME_PREFS.map((p) => (
            <MenuRadioItem key={p} value={p}>
              {t(`theme.${p}`)}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        <MenuSeparator />
        <MenuLabel>{t('shell.language')}</MenuLabel>
        <MenuRadioGroup value={i18n.language} onValueChange={(v) => setLanguage(v as 'en' | 'nl')}>
          {LANGUAGES.map((l) => (
            <MenuRadioItem key={l.code} value={l.code}>
              {l.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        <MenuSeparator />
        <MenuItem
          onSelect={() => void navigate({ to: '/settings/$section', params: { section: 'security' } })}
        >
          {t('settings.security')}
        </MenuItem>
        {me.billing?.enabled ? (
          <MenuItem onSelect={() => void navigate({ to: '/billing' })}>{t('billing.title')}</MenuItem>
        ) : null}
        <MenuItem onSelect={() => void signOut()} className="text-danger">
          {t('shell.signOut')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
